import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, type LeaderboardEntry, type LeaderboardData } from "@/lib/api";
import { PulseBeams, type BeamPath } from "@/components/ui/PulseBeams";
import {
  Trophy, Flame, Calendar, CalendarDays, Crown, TrendingUp,
  Zap, Swords, Target, Rocket, ChevronRight,
} from "lucide-react";

type Period = "week" | "month" | "all_time";

const PERIOD_CONFIG: Record<Period, { label: string; icon: typeof Calendar; desc: string }> = {
  week:     { label: "This Week",   icon: Calendar,     desc: "Monday – Sunday" },
  month:    { label: "This Month",  icon: CalendarDays,  desc: new Date().toLocaleString("default", { month: "long", year: "numeric" }) },
  all_time: { label: "All Time",    icon: TrendingUp,    desc: "Since launch" },
};

const AVATAR_COLORS = [
  "from-[#7573F7] to-[#4E42DC]",
  "from-[#05FAFC] to-[#7573F7]",
  "from-[#FD1C59] to-[#FF5387]",
  "from-[#C3A0F2] to-[#622E80]",
  "from-[#CAF171] to-[#05FAFC]",
  "from-[#FFE14D] to-[#FD1C59]",
];

function initials(name: string): string {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
}

// ─── Avatar with headshot fallback ─────────────────────────────────────────
function BdrAvatar({
  name,
  photoUrl,
  colorClass,
  size = "md",
  glowColor,
}: {
  name: string;
  photoUrl?: string | null;
  colorClass: string;
  size?: "sm" | "md" | "lg";
  glowColor?: string;
}) {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = {
    sm: "w-10 h-10 rounded-lg text-sm",
    md: "w-14 h-14 sm:w-16 sm:h-16 rounded-xl text-lg sm:text-xl",
    lg: "w-18 h-18 sm:w-20 sm:h-20 rounded-2xl text-xl sm:text-2xl",
  };

  const showPhoto = photoUrl && !imgError;

  return (
    <div
      className={`${sizeClasses[size]} bg-gradient-to-br ${colorClass} flex items-center justify-center text-white font-extrabold shadow-lg overflow-hidden shrink-0`}
      style={glowColor ? { boxShadow: `0 4px 20px ${glowColor}` } : undefined}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        initials(name)
      )}
    </div>
  );
}

// ─── PulseBeams config for hero ────────────────────────────────────────────
const heroBeams: BeamPath[] = [
  {
    path: "M429 300 C 350 260, 200 180, 30 60",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "30"], y1: ["300", "60"], x2: ["429", "200"], y2: ["300", "180"] },
      transition: { duration: 4, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 0 },
    },
  },
  {
    path: "M429 300 C 520 260, 660 180, 828 60",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "828"], y1: ["300", "60"], x2: ["429", "660"], y2: ["300", "180"] },
      transition: { duration: 3.5, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 0.5 },
    },
  },
  {
    path: "M429 300 C 320 280, 150 260, 0 250",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "0"], y1: ["300", "250"], x2: ["429", "150"], y2: ["300", "260"] },
      transition: { duration: 5, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 1.0 },
    },
  },
  {
    path: "M429 300 C 540 280, 710 260, 858 250",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "858"], y1: ["300", "250"], x2: ["429", "710"], y2: ["300", "260"] },
      transition: { duration: 4.5, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 1.5 },
    },
  },
  {
    path: "M429 300 C 429 240, 429 150, 429 10",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "429"], y1: ["300", "10"], x2: ["429", "429"], y2: ["300", "150"] },
      transition: { duration: 3, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 0.3 },
    },
  },
  {
    path: "M429 300 C 300 320, 140 370, 20 420",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "20"], y1: ["300", "420"], x2: ["429", "140"], y2: ["300", "370"] },
      transition: { duration: 4.2, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 2.0 },
    },
  },
  {
    path: "M429 300 C 560 320, 720 370, 838 420",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "838"], y1: ["300", "420"], x2: ["429", "720"], y2: ["300", "370"] },
      transition: { duration: 3.8, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 0.8 },
    },
  },
  {
    path: "M429 300 C 380 200, 260 100, 160 20",
    gradientConfig: {
      initial: { x1: "429", y1: "300", x2: "429", y2: "300" },
      animate: { x1: ["429", "160"], y1: ["300", "20"], x2: ["429", "260"], y2: ["300", "100"] },
      transition: { duration: 3.3, repeat: Infinity, repeatType: "loop", ease: "linear", delay: 1.8 },
    },
  },
];

// ─── Floating Particles ────────────────────────────────────────────────────
function FloatingParticles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 8 + 6,
    delay: Math.random() * 4,
    opacity: Math.random() * 0.4 + 0.1,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            background: `radial-gradient(circle, rgba(5, 250, 252, ${p.opacity}), rgba(117, 115, 247, ${p.opacity * 0.5}))`,
            boxShadow: `0 0 ${p.size * 3}px rgba(5, 250, 252, ${p.opacity * 0.5})`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [p.opacity, p.opacity * 1.5, p.opacity],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            delay: p.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// ─── Podium (top 3) ────────────────────────────────────────────────────────
function Podium({
  entries,
  streaks,
}: {
  entries: LeaderboardEntry[];
  streaks: Record<string, number>;
}) {
  const top3 = entries.slice(0, 3);
  if (top3.length === 0) return null;

  // Reorder for podium display: 2nd, 1st, 3rd
  const podiumOrder = top3.length >= 3
    ? [top3[1], top3[0], top3[2]]
    : top3.length === 2
      ? [top3[1], top3[0]]
      : [top3[0]];

  const podiumHeights = top3.length >= 3
    ? [140, 180, 110]
    : top3.length === 2
      ? [140, 180]
      : [180];

  const podiumRanks = top3.length >= 3
    ? [2, 1, 3]
    : top3.length === 2
      ? [2, 1]
      : [1];

  const medalColors: Record<number, string> = {
    1: "from-[#FFE14D] to-[#FFB800]",
    2: "from-[#C0C0C0] to-[#8A8A8A]",
    3: "from-[#CD7F32] to-[#A0522D]",
  };

  const glowColors: Record<number, string> = {
    1: "rgba(255, 225, 77, 0.3)",
    2: "rgba(192, 192, 192, 0.2)",
    3: "rgba(205, 127, 50, 0.2)",
  };

  return (
    <div className="flex items-end justify-center gap-3 sm:gap-6 mt-6 mb-2 px-4">
      {podiumOrder.map((entry, i) => {
        const rank = podiumRanks[i];
        const height = podiumHeights[i];
        const streak = streaks[entry.bdr_id] ?? 0;

        return (
          <motion.div
            key={entry.bdr_id}
            className="flex flex-col items-center"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.15, ease: "easeOut" }}
          >
            {/* Avatar + crown */}
            <div className="relative mb-3">
              {rank === 1 && (
                <motion.div
                  className="absolute -top-5 left-1/2 -translate-x-1/2"
                  animate={{ y: [0, -3, 0], rotate: [0, 5, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Crown size={24} className="text-[#FFE14D] drop-shadow-lg" fill="#FFE14D" />
                </motion.div>
              )}
              <BdrAvatar
                name={entry.bdr_name}
                photoUrl={entry.photo_url}
                colorClass={AVATAR_COLORS[(rank - 1) % AVATAR_COLORS.length]}
                size="md"
                glowColor={glowColors[rank]}
              />

              {/* Medal badge */}
              <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br ${medalColors[rank]} flex items-center justify-center shadow-md`}>
                <span className="text-[10px] font-extrabold text-white">{rank}</span>
              </div>
            </div>

            {/* Name */}
            <span className="text-white font-bold text-xs sm:text-sm text-center mb-1 max-w-[90px] sm:max-w-[120px] truncate">
              {entry.bdr_name}
            </span>

            {/* Count + streak */}
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-seafoam font-extrabold text-sm">{entry.count}</span>
              {streak > 0 && (
                <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#FD1C59]">
                  <Flame size={10} /> {streak}d
                </span>
              )}
            </div>

            {/* Podium pillar */}
            <motion.div
              className="relative w-20 sm:w-28 rounded-t-lg overflow-hidden"
              style={{ height }}
              initial={{ height: 0 }}
              animate={{ height }}
              transition={{ duration: 0.8, delay: 0.3 + i * 0.15, ease: "easeOut" }}
            >
              <div className={`absolute inset-0 ${
                rank === 1
                  ? "bg-gradient-to-b from-[#7573F7] to-[#3D215C]"
                  : rank === 2
                    ? "bg-gradient-to-b from-[#5A4D8C] to-[#2A1848]"
                    : "bg-gradient-to-b from-[#4A3D6C] to-[#1E1338]"
              }`} />
              {/* Pillar shine */}
              <div className="absolute inset-y-0 left-0 w-[1px] bg-gradient-to-b from-white/20 via-white/5 to-transparent" />
              <div className="absolute inset-y-0 right-0 w-[1px] bg-gradient-to-b from-white/10 via-white/0 to-transparent" />
              {/* Rank number */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 text-white/20 text-3xl sm:text-4xl font-extrabold">
                {rank}
              </div>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
export function Leaderboard() {
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [period, setPeriod] = useState<Period>("week");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.leaderboard()
      .then(setData)
      .catch((e) => setErr(e.message ?? "Failed to load leaderboard"));
  }, []);

  const entries = data ? data[period] : [];
  const maxCount = Math.max(...entries.map((e) => e.count), 1);
  const remaining = entries.slice(3); // Everyone below top 3

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0118]">
      {/* Header — dark themed */}
      <header className="bg-[#0A0118]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 shrink-0">
            <img src="/logo-icon.svg" alt="" height={32} width={32} style={{ height: 32, width: 32 }} />
            <img
              src="/logo-negative-tagline.svg"
              alt="Channelscaler"
              className="hidden sm:block"
              style={{ height: 20 }}
            />
          </a>
          <a href="/" className="text-xs text-white/50 hover:text-white/80 transition-colors flex items-center gap-1">
            Back to Portal <ChevronRight size={12} />
          </a>
        </div>
      </header>

      {/* Hero Section — full dark with PulseBeams */}
      <div className="relative overflow-hidden">
        {/* Radial gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 50% 80%, rgba(117, 115, 247, 0.15) 0%, rgba(61, 33, 92, 0.08) 40%, transparent 70%)",
          }}
        />

        {/* PulseBeams */}
        <div className="absolute inset-0 opacity-40">
          <PulseBeams
            beams={heroBeams}
            className="!h-full"
            baseColor="rgba(117, 115, 247, 0.08)"
            accentColor="rgba(195, 160, 242, 0.12)"
            gradientColors={{ start: "#05FAFC", middle: "#7573F7", end: "#C3A0F2" }}
          />
        </div>

        <FloatingParticles />

        <div className="relative z-10 pt-10 pb-4">
          {/* Title */}
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-2">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <Trophy size={36} className="text-[#FFE14D] drop-shadow-[0_0_12px_rgba(255,225,77,0.4)]" />
              </motion.div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Meeting <span className="bg-gradient-to-r from-[#05FAFC] to-[#7573F7] bg-clip-text text-transparent">Leaderboard</span>
              </h1>
            </div>
            <p className="text-white/40 text-sm font-medium">
              Discovery meetings booked — who's dominating?
            </p>
          </motion.div>

          {/* Podium */}
          {data && entries.length > 0 && (
            <Podium entries={entries} streaks={data.streaks} />
          )}
        </div>

        {/* Horizon line */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#7573F7]/30 to-transparent" />
      </div>

      {/* Period Switcher */}
      <div className="sticky top-[57px] z-40 bg-[#0A0118]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-3xl mx-auto flex">
          {(Object.keys(PERIOD_CONFIG) as Period[]).map((p) => {
            const cfg = PERIOD_CONFIG[p];
            const Icon = cfg.icon;
            const active = p === period;
            return (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-bold transition-all relative
                  ${active
                    ? "text-[#05FAFC]"
                    : "text-white/30 hover:text-white/60"
                  }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{cfg.label}</span>
                <span className="sm:hidden">{cfg.label.split(" ")[1] ?? cfg.label}</span>
                {active && (
                  <motion.div
                    layoutId="period-indicator"
                    className="absolute bottom-0 left-4 right-4 h-[2px] rounded-t bg-gradient-to-r from-[#05FAFC] to-[#7573F7]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <main className="flex-1 px-4 py-6">
        <div className="max-w-3xl mx-auto">
          {/* Loading / Error */}
          {err && (
            <div className="rounded-xl border border-[#FD1C59]/20 bg-[#FD1C59]/5 p-6 text-[#FD1C59] text-center font-bold">
              {err}
            </div>
          )}
          {!data && !err && (
            <div className="text-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Target size={32} className="text-[#7573F7]" />
              </motion.div>
              <p className="text-white/30 mt-3 font-medium">Loading rankings…</p>
            </div>
          )}

          {/* Remaining rows (after top 3) */}
          <AnimatePresence mode="wait">
            {data && (
              <motion.div
                key={period}
                className="space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
              >
                {entries.length === 0 && (
                  <div className="text-center py-16">
                    <Swords size={48} className="text-white/10 mx-auto mb-4" />
                    <p className="text-white/30 text-lg font-bold mb-1">No meetings booked yet</p>
                    <p className="text-white/20 text-sm">
                      {PERIOD_CONFIG[period].desc} — be the first to get on the board.
                    </p>
                  </div>
                )}

                {remaining.map((entry, i) => (
                  <RankRow
                    key={entry.bdr_id}
                    entry={entry}
                    rank={i + 4}
                    maxCount={maxCount}
                    colorClass={AVATAR_COLORS[(i + 3) % AVATAR_COLORS.length]}
                    streak={data.streaks[entry.bdr_id] ?? 0}
                    index={i}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats Strip */}
          {data && entries.length > 0 && (
            <motion.div
              className="grid grid-cols-3 gap-3 mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <GlowStat
                label="Total Booked"
                value={entries.reduce((s, e) => s + e.count, 0)}
                icon={<Zap size={20} />}
                color="#05FAFC"
              />
              <GlowStat
                label="BDRs Active"
                value={entries.filter((e) => e.count > 0).length}
                icon={<Rocket size={20} />}
                color="#7573F7"
              />
              <GlowStat
                label="Best Streak"
                value={`${Math.max(...Object.values(data.streaks), 0)}d`}
                icon={<Flame size={20} />}
                color="#FD1C59"
              />
            </motion.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 text-center text-[11px] text-white/20">
          Updated in real time · Channelscaler BDR Portal
        </div>
      </footer>
    </div>
  );
}

// ─── Rank Row (4th place and below) ────────────────────────────────────────
function RankRow({
  entry, rank, maxCount, colorClass, streak, index,
}: {
  entry: LeaderboardEntry;
  rank: number;
  maxCount: number;
  colorClass: string;
  streak: number;
  index: number;
}) {
  const pct = maxCount > 0 ? (entry.count / maxCount) * 100 : 0;

  return (
    <motion.div
      className="group relative rounded-xl border border-white/5 bg-white/[0.02] backdrop-blur-sm p-4 flex items-center gap-4 hover:bg-white/[0.05] hover:border-white/10 transition-all"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
    >
      {/* Rank */}
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-sm font-extrabold text-white/30 shrink-0">
        {rank}
      </div>

      {/* Avatar */}
      <BdrAvatar
        name={entry.bdr_name}
        photoUrl={entry.photo_url}
        colorClass={colorClass}
        size="sm"
      />

      {/* Name + bar */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-bold text-white/90 truncate text-sm">{entry.bdr_name}</span>
          {streak > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#FD1C59]">
              <Flame size={10} /> {streak}d
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-4 bg-white/5 rounded-full overflow-hidden relative">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#05FAFC]/80 to-[#7573F7]/80 flex items-center justify-end pr-2"
            initial={{ width: 0 }}
            animate={{ width: `${Math.max(pct, entry.count > 0 ? 12 : 0)}%` }}
            transition={{ duration: 0.8, delay: 0.2 + index * 0.06, ease: "easeOut" }}
          >
            {entry.count > 0 && (
              <span className="text-[10px] font-extrabold text-white drop-shadow whitespace-nowrap">
                {entry.count}
              </span>
            )}
          </motion.div>
          {entry.count === 0 && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white/20">
              0
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Glow Stat Card ────────────────────────────────────────────────────────
function GlowStat({
  label, value, icon, color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div
      className="relative rounded-xl border border-white/5 bg-white/[0.02] p-4 text-center overflow-hidden"
    >
      {/* Glow */}
      <div
        className="absolute -top-8 left-1/2 -translate-x-1/2 w-24 h-24 rounded-full blur-2xl opacity-20"
        style={{ background: color }}
      />
      <div className="relative">
        <div className="flex justify-center mb-2" style={{ color }}>{icon}</div>
        <div className="text-2xl font-extrabold text-white">{value}</div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/30 mt-0.5">{label}</div>
      </div>
    </div>
  );
}
